import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import GbetBanner from "@/components/GbetBanner";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Clock, AlertTriangle, Trophy, Lock, Pencil, ShoppingCart } from "lucide-react";

const ViewCodePage = () => {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  useEffect(() => {
    if (!result?.expires_at) return;
    const updateTimer = () => {
      const now = new Date();
      const exp = new Date(result.expires_at);
      const diff = Math.max(0, Math.floor((exp - now) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) {
        clearInterval(timerRef.current);
        setResult(null);
        setError("Your code has expired. The games are no longer available.");
      }
    };
    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);
    return () => clearInterval(timerRef.current);
  }, [result?.expires_at]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error("Enter your code"); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(`${API}/purchase/view`, { code: code.trim() });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid or expired code");
    } finally { setLoading(false); }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="view-code-page">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <AdvertBanner />
        <div className="max-w-2xl mx-auto">
          {!result ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Eye className="h-8 w-8 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="page-title">View Your Paid Games</h1>
                <p className="text-gray-500">Enter your one-time view code to see your games</p>
              </div>
              <Card className="bg-white border-gray-200">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Your Code</Label>
                      <Input
                        value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                        placeholder="FD-XXXXXXXX" className="h-14 text-center text-2xl font-mono tracking-widest"
                        data-testid="code-input"
                      />
                    </div>
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 flex items-start gap-2" data-testid="code-error">
                        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}
                    <Button type="submit" disabled={loading} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-full text-base font-semibold" data-testid="submit-code-btn">
                      {loading ? "Verifying..." : "View Games"}
                    </Button>
                  </form>
                  <div className="mt-6 bg-gray-50 rounded-lg p-4 space-y-2 text-sm text-gray-500">
                    <p className="font-medium text-gray-700 flex items-center gap-1"><Lock className="h-4 w-4" /> How it works:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Enter the code you received after purchase</li>
                      <li>Your games will display for <strong>30 minutes</strong></li>
                      <li>Write them down immediately — the code expires permanently after</li>
                    </ul>
                  </div>
                  <div className="mt-5 text-center border-t border-gray-100 pt-5" data-testid="get-code-cta">
                    <p className="text-gray-500 text-sm">Don't have a code?</p>
                    <Link to="/buy" className="inline-flex items-center gap-1.5 mt-1 text-green-600 hover:text-green-700 font-semibold text-sm transition-colors" data-testid="get-code-link">
                      <ShoppingCart className="h-4 w-4" /> Get a code here
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div data-testid="games-display">
              {/* Timer Banner */}
              <div className={`rounded-xl p-4 mb-6 flex items-center justify-between ${timeLeft && timeLeft < 300 ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
                <div className="flex items-center gap-2">
                  <Clock className={`h-5 w-5 ${timeLeft && timeLeft < 300 ? "text-red-600" : "text-amber-600"}`} />
                  <span className={`font-semibold text-sm ${timeLeft && timeLeft < 300 ? "text-red-700" : "text-amber-700"}`}>
                    Time remaining to view
                  </span>
                </div>
                <span className={`text-2xl font-bold font-mono countdown ${timeLeft && timeLeft < 300 ? "text-red-600" : "text-amber-600"}`} data-testid="countdown-timer">
                  {timeLeft !== null ? formatTime(timeLeft) : "--:--"}
                </span>
              </div>

              {/* Write it down notice */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                <Pencil className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">Write these down now!</p>
                  <p className="text-sm text-green-700">This is a one-time view. After the timer expires, you cannot access these games again.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {result.package_name} — Your Games
              </h2>

              {/* Total Combined Odds banner — visible for any package with games */}
              {result.games?.length > 0 && (() => {
                const totalOdds = result.games.reduce((acc, g) => {
                  const o = parseFloat(g.odds);
                  return acc * (isNaN(o) || o <= 0 ? 1 : o);
                }, 1);
                if (totalOdds <= 1.01) return null;
                const stakes = [5, 10, 50, 100];
                return (
                  <div className="mb-5 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-700 text-white p-5 shadow-md" data-testid="total-odds-banner">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="text-xs uppercase tracking-wider opacity-80">Total Combined Odds</p>
                        <p className="text-4xl font-extrabold leading-tight">{totalOdds.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-wider opacity-80">{result.games.length} games · Pays</p>
                        <p className="text-lg font-bold">{(totalOdds * 10).toFixed(2)}× on $10</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 pt-3 border-t border-white/20">
                      {stakes.map(s => (
                        <div key={s} className="text-center" data-testid={`stake-row-${s}`}>
                          <p className="text-[10px] uppercase opacity-75">Stake ${s}</p>
                          <p className="text-sm font-bold">${(totalOdds * s).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {result.games?.length > 0 ? (
                <div className="space-y-3">
                  {result.games.map((game, i) => (
                    <Card key={i} className="bg-white border-gray-200" data-testid={`game-${i}`}>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center font-bold text-sm text-green-700">{i + 1}</div>
                            <div>
                              <p className="font-bold text-gray-900">{game.home_team} vs {game.away_team}</p>
                              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                {game.league && <span className="text-xs text-gray-500">{game.league}</span>}
                                {game.kickoff && <span className="text-xs text-amber-600 font-medium">{game.kickoff}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-green-100 text-green-700 font-bold text-base px-3 py-1">{game.prediction}</Badge>
                            {game.odds && <p className="text-xs text-gray-500 mt-1">Odds: {game.odds}</p>}
                          </div>
                        </div>
                        {(game.booking_betking || game.booking_bet9ja || game.booking_sportybet || game.booking_1xbet) && (
                          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                            <span className="text-xs text-gray-500 font-medium">Booking Codes:</span>
                            {game.booking_betking && <span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2.5 py-1 font-mono font-medium">BetKing: {game.booking_betking}</span>}
                            {game.booking_bet9ja && <span className="text-xs bg-green-50 text-green-700 rounded-full px-2.5 py-1 font-mono font-medium">Bet9ja: {game.booking_bet9ja}</span>}
                            {game.booking_sportybet && <span className="text-xs bg-amber-50 text-amber-700 rounded-full px-2.5 py-1 font-mono font-medium">SportyBet: {game.booking_sportybet}</span>}
                            {game.booking_1xbet && <span className="text-xs bg-purple-50 text-purple-700 rounded-full px-2.5 py-1 font-mono font-medium">1xBet: {game.booking_1xbet}</span>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-white border-gray-200">
                  <CardContent className="py-12 text-center">
                    <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">{result.message || "Games are being prepared."}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="px-4"><div className="max-w-3xl mx-auto"><GbetBanner /></div></div>
      <Footer />
    </div>
  );
};

export default ViewCodePage;
